import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  EnvelopeIcon,
  ClockIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const emptyContactForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  tags: '',
  notes: ''
};

const TABS = [
  { key: 'contacts', label: 'Contacts', icon: UserGroupIcon },
  { key: 'compose', label: 'Send Email', icon: EnvelopeIcon },
  { key: 'history', label: 'History', icon: ClockIcon }
];

const Contacts = () => {
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('token');
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const fileInputRef = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('contacts');

  // Contacts tab UI
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Compose tab UI
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [composeTags, setComposeTags] = useState([]);
  const [sending, setSending] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get('/api/organizations/my-organizations', { headers: authHeaders });
      const orgs = Array.isArray(response.data) ? response.data : [];
      const transformed = orgs.map(orgUser => (orgUser.Organization
        ? { id: orgUser.Organization.id, name: orgUser.Organization.name }
        : orgUser));
      setOrganizations(transformed);
      setSelectedOrg(prev => prev || (transformed[0] ? transformed[0].id.toString() : ''));
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    }
  }, [authHeaders]);

  const fetchContacts = useCallback(async () => {
    if (!token || !selectedOrg) return;
    try {
      setLoading(true);
      const response = await axios.get(`/api/contacts?organization_id=${selectedOrg}`, { headers: authHeaders });
      setContacts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [token, selectedOrg, authHeaders]);

  const fetchCampaigns = useCallback(async () => {
    if (!token || !selectedOrg) return;
    try {
      const response = await axios.get(`/api/contacts/campaigns?organization_id=${selectedOrg}`, { headers: authHeaders });
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, [token, selectedOrg, authHeaders]);

  useEffect(() => {
    if (token) fetchOrganizations();
  }, [token, fetchOrganizations]);

  useEffect(() => {
    fetchContacts();
    fetchCampaigns();
  }, [fetchContacts, fetchCampaigns]);

  // ── Derived values ────────────────────────────────────────────────────────

  const allTags = useMemo(() => {
    const map = new Map();
    contacts.forEach(c => (c.tags || []).forEach(t => {
      const key = String(t).toLowerCase();
      if (!map.has(key)) map.set(key, t);
    }));
    return [...map.values()].sort((a, b) => a.localeCompare(b));
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return contacts.filter(c => {
      const matchesSearch = !term ||
        `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term);
      const matchesTag = !tagFilter || (c.tags || []).some(t => String(t).toLowerCase() === tagFilter.toLowerCase());
      return matchesSearch && matchesTag;
    });
  }, [contacts, searchTerm, tagFilter]);

  const composeRecipientCount = useMemo(() => {
    return contacts.filter(c => c.subscribed && (
      composeTags.length === 0 ||
      (c.tags || []).some(t => composeTags.map(x => x.toLowerCase()).includes(String(t).toLowerCase()))
    )).length;
  }, [contacts, composeTags]);

  const subscribedCount = useMemo(() => contacts.filter(c => c.subscribed).length, [contacts]);

  // ── Contact CRUD ────────────────────────────────────────────────────────────

  const openCreateContact = () => {
    setEditingContact(null);
    setContactForm(emptyContactForm);
    setShowContactForm(true);
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      tags: (contact.tags || []).join(', '),
      notes: contact.notes || ''
    });
    setShowContactForm(true);
  };

  const saveContact = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      if (editingContact) {
        await axios.put(`/api/contacts/${editingContact.id}`, {
          ...contactForm,
          tags: contactForm.tags
        }, { headers: authHeaders });
        toast.success('Contact updated');
      } else {
        await axios.post('/api/contacts', {
          organization_id: parseInt(selectedOrg, 10),
          ...contactForm,
          tags: contactForm.tags
        }, { headers: authHeaders });
        toast.success('Contact added');
      }
      setShowContactForm(false);
      setEditingContact(null);
      setContactForm(emptyContactForm);
      fetchContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(error.response?.data?.message || 'Failed to save contact');
    }
  };

  const deleteContact = async (contact) => {
    if (!window.confirm(`Delete ${contact.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/contacts/${contact.id}`, { headers: authHeaders });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error(error.response?.data?.message || 'Failed to delete contact');
    }
  };

  const toggleSubscribed = async (contact) => {
    try {
      await axios.put(`/api/contacts/${contact.id}`, { subscribed: !contact.subscribed }, { headers: authHeaders });
      fetchContacts();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  // ── CSV import ────────────────────────────────────────────────────────────

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOrg) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('csvFile', file);
      formData.append('organization_id', selectedOrg);
      const response = await axios.post('/api/contacts/import', formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      toast.success(response.data.message || 'Import complete');
      fetchContacts();
    } catch (error) {
      console.error('Error importing contacts:', error);
      toast.error(error.response?.data?.message || 'Failed to import contacts');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get('/api/contacts/template', { headers: authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // ── Compose / send ──────────────────────────────────────────────────────────

  const toggleComposeTag = (tag) => {
    setComposeTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const sendCampaign = async () => {
    if (!selectedOrg) return;
    if (!composeSubject.trim() || !composeMessage.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    if (composeRecipientCount === 0) {
      toast.error('No subscribed contacts match the selected recipients');
      return;
    }
    if (!window.confirm(`Send this email to ${composeRecipientCount} contact${composeRecipientCount !== 1 ? 's' : ''}?`)) return;

    setSending(true);
    try {
      const response = await axios.post('/api/contacts/campaigns/send', {
        organization_id: parseInt(selectedOrg, 10),
        subject: composeSubject,
        message: composeMessage,
        tags: composeTags
      }, { headers: authHeaders });
      toast.success(response.data.message || 'Email sent');
      setComposeSubject('');
      setComposeMessage('');
      setComposeTags([]);
      fetchCampaigns();
      setActiveTab('history');
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return '—';
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <UserGroupIcon className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="text-sm text-gray-500">Import contacts and send bulk emails to prospects, alumni, and families.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select an organization</option>
          {organizations.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>

      {!selectedOrg ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          Select an organization to manage contacts.
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 mb-6">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                    active
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.key === 'contacts' && contacts.length > 0 && (
                    <span className="ml-1 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{contacts.length}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Contacts tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                <div className="flex flex-1 gap-3">
                  <div className="relative flex-1 max-w-xs">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search name or email"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <select
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All tags</option>
                    {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <ArrowDownTrayIcon className="h-4 w-4" /> Template
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" /> {importing ? 'Importing…' : 'Import CSV'}
                  </button>
                  <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImportFile} className="hidden" />
                  <button onClick={openCreateContact} className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700">
                    <PlusIcon className="h-4 w-4" /> Add Contact
                  </button>
                </div>
              </div>

              {importResult && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <p className="font-medium">{importResult.message}</p>
                  {importResult.validationErrors?.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-emerald-700">{importResult.validationErrors.length} row(s) skipped during validation</summary>
                      <ul className="mt-1 list-disc list-inside text-emerald-700">
                        {importResult.validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading contacts…</div>
              ) : filteredContacts.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
                  {contacts.length === 0 ? 'No contacts yet. Import a CSV or add one manually.' : 'No contacts match your filters.'}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredContacts.map(contact => (
                        <tr key={contact.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{contact.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{contact.phone || '—'}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex flex-wrap gap-1">
                              {(contact.tags || []).map(tag => (
                                <span key={tag} className="text-xs px-2 py-0.5 bg-primary-50 text-primary-700 rounded">{tag}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <button
                              onClick={() => toggleSubscribed(contact)}
                              className={`text-xs px-2 py-0.5 rounded-full ${contact.subscribed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}
                              title="Toggle subscription"
                            >
                              {contact.subscribed ? 'Subscribed' : 'Unsubscribed'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => openEditContact(contact)} className="p-1.5 text-gray-500 hover:text-gray-700" title="Edit">
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button onClick={() => deleteContact(contact)} className="p-1.5 text-gray-500 hover:text-red-600" title="Delete">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Compose tab */}
          {activeTab === 'compose' && (
            <div className="bg-white rounded-lg shadow p-6 space-y-5 max-w-3xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="e.g. Fall 2026 tryouts are open!"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={composeMessage}
                  onChange={(e) => setComposeMessage(e.target.value)}
                  rows={10}
                  placeholder={'Hi there,\n\nWrite your announcement here...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Tip: use <code className="bg-gray-100 px-1 rounded">{'{{first_name}}'}</code> to personalize the greeting. An unsubscribe link is added automatically.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recipients</label>
                {allTags.length === 0 ? (
                  <p className="text-sm text-gray-500">All {subscribedCount} subscribed contact{subscribedCount !== 1 ? 's' : ''} will receive this email.</p>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 mb-2">Leave all tags unselected to email every subscribed contact, or pick tags to target a segment.</p>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map(tag => {
                        const active = composeTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleComposeTag(tag)}
                            className={`text-sm px-3 py-1 rounded-full border transition ${
                              active
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  This will send to <span className="font-semibold text-gray-900">{composeRecipientCount}</span> subscribed contact{composeRecipientCount !== 1 ? 's' : ''}.
                </p>
                <button
                  onClick={sendCampaign}
                  disabled={sending || composeRecipientCount === 0}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  {sending ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </div>
          )}

          {/* History tab */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {campaigns.length === 0 ? (
                <div className="p-12 text-center text-gray-500">No emails sent yet.</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Segment</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {campaigns.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{c.subject}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {(c.segment_tags && c.segment_tags.length > 0) ? c.segment_tags.join(', ') : 'All subscribed'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {c.sent_count}/{c.recipient_count}
                          {c.failed_count > 0 && <span className="text-red-600"> ({c.failed_count} failed)</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{c.SentByUser?.name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(c.sent_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Contact form modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{editingContact ? 'Edit Contact' : 'Add Contact'}</h3>
              <button onClick={() => { setShowContactForm(false); setEditingContact(null); }} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={saveContact} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                  <input type="text" value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                  <input type="text" value={contactForm.last_name} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input type="text" value={contactForm.tags} onChange={(e) => setContactForm({ ...contactForm, tags: e.target.value })} placeholder="alumni, 2024 tryout, prospect" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <p className="mt-1 text-xs text-gray-500">Separate tags with commas. Tags are used to target email segments.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowContactForm(false); setEditingContact(null); }} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">{editingContact ? 'Save' : 'Add Contact'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
